<template>
    <div v-if="color" class="c-solid-color-picker-wrapper">
        <div class="left-panel">
            <div class="c-saturation-wrapper">
                <saturation-board v-model="color" @change="onColorChange" />
            </div>
            <div class="c-picker-controls">
                <div class="c-sliders">
                    <div class="c-hue-wrapper">
                        <hue-slider :colors="color" @change="onColorChange" />
                    </div>
                    <div class="c-alpha-wrapper">
                        <alpha-slider :colors="color" @change="onColorChange" />
                    </div>
                </div>
                <div class="c-color-preview" :style="{ 'background-color': rbgPreview }" />
            </div>
        </div>
        <div class="right-panel">
            <c-input
                v-for="(_, key, idx) of inputModeSet"
                :key="`input-${key}-${idx}`"
                v-model="inputModeSet[key]"
                :label="key.toUpperCase()"
                class="mb-2"
                type="number"
                step=".01"
                input-size="sm"
                inline
                @input="onColorChange({ [mode]: inputModeSet })"
            />

            <c-input
                v-model="inputModel.alpha"
                inline
                label="A"
                type="number"
                :min="0"
                :max="1"
                input-size="sm"
                @input="onColorChange({ hsl: color.hsl, a: Number($event) })"
            />

            <c-input
                v-if="mode !== 'cmyk'"
                v-model="inputModel.hex"
                class="mt-2"
                inline
                label="#"
                input-size="sm"
                @blur="onColorChange({ hex: `${$event}` })"
                @enter="onColorChange({ hex: `${$event}` })"
            />

            <div class="mt-auto ml-auto mb-2">
                <c-button size="icon" :icon="['fas', 'eye-dropper']" @click="openEyedropper" />
                <c-button v-if="allowReset" variant="secondary" :text="resetText" @click="onReset" />
            </div>
            <slot name="footer" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, reactive } from 'vue';
import { type FullColorObject, useColor } from '@/composables/color';
import SaturationBoard from './SaturationBoard.vue';
import HueSlider from './HueSlider.vue';
import AlphaSlider from './AlphaSlider.vue';
import CInput from '../CInput';
import CButton from '@/components/shared/CButton';

type ColorPickerMode = 'cmyk' | 'rgb';

const props = withDefaults(
    defineProps<{
        value?: FullColorObject;
        mode?: ColorPickerMode;
        initialColor?: string;
        allowReset?: boolean;
        resetText?: string;
    }>(),
    { mode: 'rgb', initialColor: '#8C2525', resetText: 'Reset' }
);

const emit = defineEmits<{
    (event: 'input', value: FullColorObject): void;
    (event: 'reset', value?: FullColorObject): void;
    (event: 'eye-dropper-used'): void;
    (event: 'using-eye-dropper'): void;
}>();

const colord = useColor();
const inputModel = reactive({
    hex: '',
    rgb: { r: 0, g: 0, b: 0 },
    cmyk: { c: 0, m: 0, y: 0, k: 0 },
    alpha: 1
});

const color = computed({
    get: () => {
        const value = props.value;
        if (!value?.hsl || !value?.hsv) return;
        return value;
    },
    set: (value: FullColorObject | undefined) => {
        emit('input', value!);
    }
});

const inputModeSet = computed((): Record<string, number> => inputModel[props.mode]);

const rbgPreview = computed(() => (color.value ? colord.hexToHexa(color.value.hex, color.value.a) : ''));

onBeforeMount(() => {
    if (!props.value) {
        onColorChange({ hex: props.initialColor, a: 1 });
        return;
    } else if (!props.value.rgb || !props.value.cmyk || !props.value.rounded) {
        onColorChange(props.value)
        return;
    }
    updateColorModel(props.value);
});

const onColorChange = (partialColor: Partial<FullColorObject>) => {
    if (partialColor.hex?.length && !partialColor.hex.includes('#')) {
        partialColor.hex = `#${partialColor.hex}`;
    }
    partialColor.a = partialColor.a ?? color.value?.a;
    const newColor = colord.getColorObj(partialColor);
    if (!newColor) return;

    updateColorModel(newColor);
};

const updateColorModel = (value: FullColorObject) => {
    color.value = value;
    inputModel.hex = value.hex.substring(1);
    inputModel.alpha = value.a;
    inputModel.rgb = { ...value.rounded.rgb };
    inputModel.cmyk = { ...value.rounded.cmyk };
};

const onReset = () => {
    emit('reset', color.value);
};

const openEyedropper = async () => {
    if (!window.EyeDropper) return;
    const eyedropper = new window.EyeDropper();
    emit('using-eye-dropper');
    const colorClicked = await eyedropper.open();
    const partialColor = { hex: colorClicked.sRGBHex, a: 1 };
    updateColorModel(colord.getColorObj(partialColor)!);
    emit('eye-dropper-used');
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-solid-color-picker-wrapper {
    display: flex;
    width: 100%;
    box-sizing: initial;
    .left-panel {
        position: relative;
        width: inherit;
        margin-right: 0.5rem;
        .c-saturation-wrapper {
            width: 100%;
            padding-bottom: 75%;
            position: relative;
            overflow: hidden;
        }
        .c-picker-controls {
            display: flex;
            margin: 0.75rem 0;
            .c-sliders {
                margin-right: 0.25rem;
                flex: 1;
                .c-hue-wrapper {
                    position: relative;
                    height: 0.9rem;
                }
                .c-alpha-wrapper {
                    position: relative;
                    height: 0.9rem;
                    margin-top: 0.5rem;
                }
            }
            .c-color-preview {
                width: 1.8rem;
                height: 1.8rem;
                align-self: center;
                border-radius: 0.25rem;
                border: 0.5px solid get-color('gray');
            }
        }
    }
    .right-panel {
        display: flex;
        flex-direction: column;
    }
}
</style>
